import { useEffect, useState } from 'react';
import { fetchAPI, postAPI } from '../../utils/utils';
import './EventCategoriesEditor.css';

const emptyForm = { name: '', filterName: '' };

export default function EventCategoriesEditor({ eventCategories, onCategoriesChanged }) {
    const [categories, setCategories] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState('');

    const load = () => {
        setLoading(true);
        setError(null);
        fetchAPI('/calendar/categories', data => {
            setCategories(data.event_categories);
            setLoading(false);
        }, false, () => {
            setError('No s’han pogut carregar les categories. Torna-ho a provar.');
            setLoading(false);
        });
    };

    useEffect(() => { load(); }, []);
    useEffect(() => {
        if (Array.isArray(eventCategories)) setCategories(eventCategories);
    }, [eventCategories]);

    const startEditing = category => {
        setEditing(category || {});
        setForm(category ? {
            name: category.labels.cat,
            filterName: category.filterLabels?.cat || '',
        } : emptyForm);
        setError(null);
        setNotice('');
    };

    const save = event => {
        event.preventDefault();
        if (saving) return;
        setSaving(true);
        setError(null);
        const labels = { cat: form.name.trim() };
        const filterLabels = { cat: form.filterName.trim() || labels.cat };
        postAPI(editing.id ? `/calendar/categories/${editing.id}` : '/calendar/categories',
            { labels, filterLabels }, data => {
                setCategories(data.event_categories);
                onCategoriesChanged?.(data.event_categories);
                setNotice(editing.id ? 'Categoria actualitzada.' : 'Categoria creada.');
                setEditing(null);
                setSaving(false);
            }, failure => {
                setError(failure?.error || 'No s’ha pogut desar la categoria. Torna-ho a provar.');
                setSaving(false);
            });
    };

    const field = (key, label, required = false, placeholder) => (
        <label className="event-categories-field" htmlFor={`category-${key}`}>
            <span>{label}</span>
            <input id={`category-${key}`} value={form[key]} required={required} maxLength={100}
                placeholder={placeholder} disabled={saving}
                onChange={event => setForm(previous => ({ ...previous, [key]: event.target.value }))} />
        </label>
    );

    return (
        <section className="event-categories-editor" aria-labelledby="event-categories-title">
            <div className="event-categories-heading">
                <div>
                    <h2 id="event-categories-title">Categories d’esdeveniments</h2>
                    <p>Crea les categories de la colla per organitzar els esdeveniments i filtrar-los a l’app.</p>
                </div>
                {categories && !editing && !loading ? (
                    <button className="event-categories-primary" onClick={() => startEditing(null)}>Afegir categoria</button>
                ) : null}
            </div>

            {error ? <p role="alert" className="event-categories-error">{error}</p> : null}
            {notice ? <p role="status" className="event-categories-success">{notice}</p> : null}
            {loading ? <p role="status">Carregant categories…</p> : null}
            {!loading && !categories ? <button onClick={load}>Tornar-ho a provar</button> : null}

            {editing ? (
                <form aria-label={editing.id ? 'Editar categoria' : 'Nova categoria'} onSubmit={save} className="event-categories-form">
                    <h3>{editing.id ? 'Editar categoria' : 'Nova categoria'}</h3>
                    <div className="event-categories-fields">
                        {field('name', 'Nom', true)}
                        {field('filterName', 'Text del filtre (opcional)', false, form.name || 'Ex.: Per a famílies')}
                    </div>
                    <p className="event-categories-help">Si deixes el text del filtre buit, s’hi mostrarà el nom de la categoria.</p>
                    {editing.id ? <p className="event-categories-help">Els esdeveniments assignats i els filtres personals es conserven en canviar el nom.</p> : null}
                    <div className="event-categories-actions">
                        <button type="submit" className="event-categories-primary" disabled={saving || !form.name.trim()}>
                            {saving ? 'Desant…' : editing.id ? 'Desar canvis' : 'Crear categoria'}
                        </button>
                        <button type="button" disabled={saving} onClick={() => { setEditing(null); setError(null); }}>Cancel·lar</button>
                    </div>
                </form>
            ) : null}

            {categories && !loading ? (
                <ul className="event-categories-list">
                    {categories.map(category => (
                        <li key={category.id}>
                            <div>
                                <strong>{category.labels.cat}</strong>
                                <p>Text del filtre: {category.filterLabels?.cat || category.labels.cat}</p>
                                {category.id === 'general' ? <small>Categoria per defecte dels esdeveniments.</small> : null}
                            </div>
                            <button disabled={Boolean(editing)} aria-label={`Editar ${category.labels.cat}`} onClick={() => startEditing(category)}>Editar</button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </section>
    );
}
